import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Spaces = () => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1] }
  };

  const spaces = isEn ? [
    {
      name: 'The Central Patio',
      subtitle: 'The beating heart of the riad',
      description: 'The nerve center of the riad, the patio is much more than a simple architectural space. It is an invitation to contemplation, a place where time slows down. The elegant arcades surrounding it create a fascinating play of shadows and light throughout the day. In the center, a traditional fountain brings that soothing note characteristic of Moroccan riads. In the morning, the first rays of sun caress the zellige floor, creating an almost mystical atmosphere. In the afternoon, this is where you will enjoy your mint tea, lulled by the song of water. In the evening, traditional lanterns transform the space into a living tableau worthy of the Thousand and One Nights.',
      image: '/images/rey0acg7_616FD1E8-33A4-4E04-A26C-D74C9EE4E5BC.JPG',
      details: ['Traditional fountain', 'Exceptional zellige', 'Majestic arcades', 'Natural light']
    },
    {
      name: 'The Moroccan Lounges',
      subtitle: 'Spaces of refined conviviality',
      description: 'Two authentic Moroccan lounges welcome you for unforgettable moments of sharing. The ground floor lounge, intimate and warm, is perfect for heartfelt conversations or a quiet reading session. Its benches covered with precious fabrics invite relaxation. The rooftop lounge, more airy, offers a unique experience: dining under the stars on soft cushions, while contemplating the sparkling lights of the medina. This is where your evenings will take on a magical dimension, between tradition and contemporary luxury.',
      image: '/images/2s55i11u_2ABB33CC-B1BB-4678-A614-5B16E824815A.JPG',
      details: ['Traditional benches', 'Refined decoration', 'Intimate atmosphere', 'Medina view']
    },
    {
      name: 'The Rooftop with Pool',
      subtitle: 'Your private oasis above the medina',
      description: 'The Riad KAMA rooftop is an experience in itself. Accessible by a traditional staircase, it reveals an exceptional space that will quickly become your favorite place. The refreshing pool invites you to lounge during hot summer days, while comfortable sun loungers are perfect for a moment of reading or a nap in the sun. The panoramic views over the rooftops of the medina, minarets and, on clear days, the snow-capped peaks of the Atlas create an unforgettable setting. This is where you will have your breakfasts facing the sunrise, where you will enjoy your dinners under the starry sky, where you will create your most beautiful Moroccan memories.',
      image: '/images/i7m3l513_8693075F-13C8-4725-A3D8-3C6B4C460578.JPG',
      details: ['Private pool', '360° panoramic view', 'Luxury sun loungers', 'Outdoor dining area']
    },
    {
      name: 'The Upper Terrace Solarium',
      subtitle: 'The culmination of your experience',
      description: 'At the top of the riad, the upper terrace solarium offers the ultimate contemplative experience. It is the highest point, your private observatory over Marrakech. Here, nothing obstructs the view: the sky stretches to infinity, the Atlas peaks draw the horizon, and the medina spreads at your feet. Ideal for sunrise yoga sessions, afternoon sunbathing, or simply admiring the sunset that sets the Moroccan sky ablaze with orange and purple hues. A suspended moment, timeless, just for you.',
      image: '/images/8028fiov_4A6A62E0-7C16-4E1E-8193-A51E85F20450.JPG',
      details: ['Atlas view', 'Yoga space', 'Spectacular sunsets', 'Absolute privacy']
    },
    {
      name: 'The Massage Room',
      subtitle: 'Your wellness sanctuary',
      description: 'Nestled in a quiet corner of the riad, the massage room is your refuge dedicated to well-being and relaxation. The space has been designed to create a soothing atmosphere conducive to deep relaxation. Subdued lighting, delicate scents, perfectly controlled temperature: every detail has been thought out for your comfort. Our professional masseuse offers traditional Moroccan treatments or contemporary massages, according to your preferences. After a day of discovery in the medina, it is the perfect experience to soothe body and mind.',
      image: '/images/ba0zqmeg_DC404373-6A69-4B8B-8B65-8E8AB7302FAE.JPG',
      details: ['Zen atmosphere', 'Professional masseuse', 'Traditional treatments', 'Natural products']
    },
    {
      name: 'The Large Terrace',
      subtitle: 'Outdoor living space',
      description: 'Between the patio and the rooftop, the large terrace is a perfect transition space. More intimate than the rooftop, but already offering that feeling of being suspended above urban life, it is ideal for small group moments. Have your morning coffee there while listening to the city wake up, or settle in late afternoon for an aperitif with friends. The lush plants adorning it create a hanging garden atmosphere, a green setting in the heart of the medina.',
      image: '/images/gjkmwhbz_058F89B8-42D5-40E8-B6D7-CA0F6ABD5A4F.JPG',
      details: ['Hanging garden', 'Dining corner', 'Clear view', 'Preserved privacy']
    }
  ] : [
    {
      name: 'Le Patio Central',
      subtitle: 'Le cœur battant du riad',
      description: 'Centre névralgique du riad, le patio est bien plus qu\'un simple espace architectural. C\'est une invitation à la contemplation, un lieu où le temps ralentit. Les arcades élégantes qui l\'entourent créent un jeu d\'ombres et de lumière fascinant tout au long de la journée. Au centre, une fontaine traditionnelle apporte cette note apaisante caractéristique des riads marocains. Le matin, les premiers rayons du soleil caressent le sol en zellige, créant une atmosphère presque mystique. L\'après-midi, c\'est ici que vous dégusterez votre thé à la menthe, bercé par le chant de l\'eau. Le soir, les lanternes traditionnelles transforment l\'espace en un tableau vivant digne des Mille et Une Nuits.',
      image: '/images/rey0acg7_616FD1E8-33A4-4E04-A26C-D74C9EE4E5BC.JPG',
      details: ['Fontaine traditionnelle', 'Zellige d\'exception', 'Arcades majestueuses', 'Lumière naturelle']
    },
    {
      name: 'Les Salons Marocains',
      subtitle: 'Espaces de convivialité raffinée',
      description: 'Deux salons marocains authentiques vous accueillent pour des moments de partage inoubliables. Le salon du rez-de-chaussée, intime et chaleureux, est parfait pour des conversations à cœur ouvert ou une séance de lecture au calme. Ses banquettes recouvertes de tissus précieux invitent à la détente. Le salon du rooftop, plus aérien, offre une expérience unique : dîner sous les étoiles sur des coussins moelleux, tout en contemplant les lumières scintillantes de la médina. C\'est ici que vos soirées prendront une dimension magique, entre tradition et luxe contemporain.',
      image: '/images/2s55i11u_2ABB33CC-B1BB-4678-A614-5B16E824815A.JPG',
      details: ['Banquettes traditionnelles', 'Décoration raffinée', 'Ambiance intimiste', 'Vue sur la médina']
    },
    {
      name: 'Le Rooftop avec Bassin',
      subtitle: 'Votre oasis privée au-dessus de la médina',
      description: 'Le rooftop du Riad KAMA est une expérience en soi. Accessible par un escalier traditionnel, il révèle un espace exceptionnel qui deviendra rapidement votre lieu préféré. Le bassin rafraîchissant vous invite à vous prélasser pendant les chaudes journées d\'été, tandis que les transats confortables sont parfaits pour un moment de lecture ou une sieste au soleil. Les vues panoramiques sur les toits de la médina, les minarets et, par temps clair, les sommets enneigés de l\'Atlas créent un décor inoubliable. C\'est ici que vous prendrez vos petits-déjeuners face au lever du soleil, que vous dégusterez vos dîners sous la voûte étoilée, que vous créerez vos plus beaux souvenirs marocains.',
      image: '/images/i7m3l513_8693075F-13C8-4725-A3D8-3C6B4C460578.JPG',
      details: ['Bassin privé', 'Vue panoramique 360°', 'Transats de luxe', 'Espace repas extérieur']
    },
    {
      name: 'La Sur-Terrasse Solarium',
      subtitle: 'Le point culminant de votre expérience',
      description: 'Au sommet du riad, la sur-terrasse solarium offre l\'ultime expérience contemplative. C\'est le point le plus élevé, votre observatoire privé sur Marrakech. Ici, rien ne vient obstruer la vue : le ciel s\'étend à l\'infini, les sommets de l\'Atlas dessinent l\'horizon, et la médina s\'étale à vos pieds. Idéal pour les séances de yoga au lever du soleil, les bains de soleil l\'après-midi, ou simplement pour admirer le coucher de soleil qui embrase le ciel marocain de teintes orange et pourpres. Un moment suspendu, hors du temps, rien que pour vous.',
      image: '/images/8028fiov_4A6A62E0-7C16-4E1E-8193-A51E85F20450.JPG',
      details: ['Vue Atlas', 'Espace yoga', 'Couchers de soleil spectaculaires', 'Intimité absolue']
    },
    {
      name: 'La Salle de Massage',
      subtitle: 'Votre sanctuaire bien-être',
      description: 'Nichée dans un coin tranquille du riad, la salle de massage est votre refuge dédié au bien-être et à la détente. L\'espace a été conçu pour créer une atmosphère apaisante propice à la relaxation profonde. Lumière tamisée, senteurs délicates, température parfaitement contrôlée : chaque détail a été pensé pour votre confort. Notre masseuse professionnelle vous propose des soins traditionnels marocains ou des massages contemporains, selon vos préférences. Après une journée de découverte dans la médina, c\'est l\'expérience parfaite pour apaiser le corps et l\'esprit.',
      image: '/images/ba0zqmeg_DC404373-6A69-4B8B-8B65-8E8AB7302FAE.JPG',
      details: ['Ambiance zen', 'Masseuse professionnelle', 'Soins traditionnels', 'Produits naturels']
    },
    {
      name: 'La Grande Terrasse',
      subtitle: 'Espace de vie en plein air',
      description: 'Entre le patio et le rooftop, la grande terrasse constitue un espace de transition parfait. Plus intime que le rooftop, mais offrant déjà cette sensation d\'être suspendu au-dessus de la vie urbaine, elle est idéale pour des moments en petit comité. Prenez-y votre café du matin en écoutant la ville s\'éveiller, ou installez-vous-y en fin d\'après-midi pour un apéritif entre amis. Les plantes luxuriantes qui l\'agrémentent créent une atmosphère de jardin suspendu, un écrin de verdure au cœur de la médina.',
      image: '/images/gjkmwhbz_058F89B8-42D5-40E8-B6D7-CA0F6ABD5A4F.JPG',
      details: ['Jardin suspendu', 'Coin repas', 'Vue dégagée', 'Intimité préservée']
    }
  ];

  return (
    <div data-testid="spaces-page" className="bg-[#FAF8F5] min-h-screen">
      {/* Hero */}
      <section className="pt-48 pb-32 px-8 md:px-16 lg:px-32">
        <div className="max-w-7xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[#B8860B] text-xs uppercase tracking-[0.3em] mb-6 text-center"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Architecture & Design
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-serif text-5xl md:text-7xl text-[#1A1A1A] mb-8 text-center"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}
          >
            {t('spaces.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-lg text-[#1A1A1A]/80 max-w-4xl mx-auto text-center leading-relaxed"
            style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}
          >
            {isEn 
              ? 'Six exceptional spaces that make up your private residence. From the majestic central patio to the panoramic rooftop, each place has been designed to create unforgettable moments and enhance your Moroccan experience.'
              : 'Six espaces exceptionnels qui composent votre demeure privée. Du patio central majestueux au rooftop panoramique, chaque lieu a été pensé pour créer des moments inoubliables et sublimer votre expérience marocaine.'}
          </motion.p>
        </div>
      </section>

      {/* Spaces */}
      <section className="pb-32 px-8 md:px-16 lg:px-32">
        <div className="max-w-7xl mx-auto space-y-32">
          {spaces.map((space, index) => (
            <motion.div
              key={index}
              {...fadeInUp}
              transition={{ ...fadeInUp.transition, delay: index * 0.1 }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className={`space-y-8 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="w-16 h-[2px] bg-[#D4AF37]"></div>
                <div>
                  <h2
                    className="font-serif text-4xl md:text-5xl text-[#1A1A1A] mb-3"
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}
                  >
                    {space.name}
                  </h2>
                  <p
                    className="text-[#B8860B] text-sm uppercase tracking-wider mb-6"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {space.subtitle}
                  </p>
                </div>
                <p
                  className="text-base text-[#1A1A1A]/80 leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '300' }}
                >
                  {space.description}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {space.details.map((detail, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#D4AF37]"></div>
                      <span className="text-sm text-[#1A1A1A]/70" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {detail}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className={`relative overflow-hidden h-[600px] border border-[#D4AF37]/30 group shadow-lg ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <img
                  src={space.image}
                  alt={space.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[2s]"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Spaces;
